import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { ButtonRounded } from '../../../UISystem/Buttons';
import { SHADOWS } from '../../../../styles/UISystem';
import { formatMetSentence, formatLocationPillPrimary } from '../../../../utils/locationFormatters';

/**
 * HostTextVenueConfirm — the TEXT variant's venue confirm as an AUTO-RAISED
 * BOTTOM SHEET (device pass 1 ruling, 2026-08-13: the full page was too
 * much, the inline row too tiny — the sheet orients without trapping).
 * Formation → People with the sheet up → Confirm / edit / Skip → card.
 *
 * The prefilled "Met at {venue}" sentence force-opens the venue gate
 * (showing the unconfirmed guess IS the confirm prompt). Tapping it opens
 * the EXISTING LocationOverlay venue picker (onEdit — zIndex 2100 sits
 * above this sheet; a pick advances the stage and the sheet leaves).
 * Backdrop tap ≡ Skip: no write, the gated formatters fall to the
 * neighborhood everywhere else. Fine-tune continues from this shape.
 *
 * Writes are the parent's (confirmVenue / changeVenue — the one venue home).
 */
const HostTextVenueConfirm = ({ connectionMetadata, onConfirm, onEdit, onSkip }) => {
  // Force-open the gate for the prompt itself — the guess is the question
  const openMeta = { ...connectionMetadata, venueConfirmed: true };
  const place = formatLocationPillPrimary(openMeta);
  const sentence = place ? formatMetSentence(place, openMeta) : '';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onSkip}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        background: 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      <motion.div
        initial={{ y: 400 }}
        animate={{ y: 0 }}
        exit={{ y: 400 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          backgroundColor: 'white',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          boxShadow: SHADOWS.TIER3,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '18px',
            padding: '28px 24px',
            paddingBottom: 'calc(28px + env(safe-area-inset-bottom))',
            maxWidth: '360px',
            margin: '0 auto',
          }}
        >
          <div style={{ fontSize: '14px', color: '#8E8E93' }}>New connection</div>

          {/* The guess as the question — tap to change (existing picker) */}
          <div
            onClick={onEdit}
            style={{
              fontSize: '22px',
              fontWeight: '700',
              color: '#000000',
              textAlign: 'center',
              cursor: 'pointer',
            }}
          >
            {sentence || 'Where did you meet?'}
          </div>

          {sentence ? (
            <ButtonRounded text="Confirm" onClick={onConfirm} variant="primary" size="large" />
          ) : (
            <ButtonRounded text="Set place" onClick={onEdit} variant="primary" size="large" />
          )}
          <ButtonRounded text="Skip" onClick={onSkip} variant="blank" size="auto" />
        </div>
      </motion.div>
    </motion.div>
  );
};

HostTextVenueConfirm.propTypes = {
  connectionMetadata: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

export default HostTextVenueConfirm;
